import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import type { IAddOn } from './mock-listings.data';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

/**
 * One entry in a host's personal add-on library — the same shape as IAddOn
 * minus the per-listing id, plus a libraryId of its own so the same item
 * can live in the library AND be attached to one or more listings (each
 * attach mints a fresh listing-side id).
 */
export interface IAddOnLibraryItem extends Omit<IAddOn, 'id'> {
  libraryId: string;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY_PREFIX = 'cnt-addon-library';
const GUEST_KEY = `${STORAGE_KEY_PREFIX}-guest`;
const IDB_PREFIX = 'addon-library';
const GUEST_IDB_KEY = `${IDB_PREFIX}:guest`;

function makeLibraryId(): string {
  return `lib-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Per-user library of add-on drafts. Stored in localStorage keyed by the
 * signed-in user's email so each host keeps their own; guests fall back to
 * a shared bucket whose entries migrate forward on sign-in. Library items
 * are never published on their own — they're one-click attached as a real
 * add-on into any listing via Phase3AddonsComponent, the standalone editor,
 * or the bulk builder.
 */
@Injectable({ providedIn: 'root' })
export class AddonLibraryService {
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService);

  private readonly store = createIdbKvStore<IAddOnLibraryItem[]>({
    legacyKeys: { [GUEST_IDB_KEY]: GUEST_KEY },
  });

  private readonly _library$ = new BehaviorSubject<IAddOnLibraryItem[]>([]);
  readonly library$: Observable<IAddOnLibraryItem[]> = this._library$.asObservable();

  private storageKey = GUEST_IDB_KEY;

  constructor() {
    // Gate on init() completing first — see quick-reply.service.ts's
    // identical constructor comment for why (an empty pre-hydration cache
    // would otherwise look indistinguishable from "no data yet").
    void this.store.init().then(() => {
      this.refreshKey();
      this.auth.currentUser$.subscribe(() => this.refreshKey());
      this.auth.loggedOut$.subscribe(() => this.wipeCurrentKey());
      this._library$.next(this.read());
    });
  }

  list(): IAddOnLibraryItem[] {
    return this._library$.value;
  }

  add(item: Omit<IAddOnLibraryItem, 'libraryId' | 'createdAt' | 'updatedAt'>): IAddOnLibraryItem {
    const now = new Date().toISOString();
    const next: IAddOnLibraryItem = { ...item, libraryId: makeLibraryId(), createdAt: now, updatedAt: now };
    const all = [...this._library$.value, next];
    this.write(all);
    return next;
  }

  update(libraryId: string, patch: Partial<Omit<IAddOnLibraryItem, 'libraryId' | 'createdAt'>>): IAddOnLibraryItem | null {
    const all = this._library$.value.slice();
    const idx = all.findIndex(i => i.libraryId === libraryId);
    if (idx === -1) return null;
    const updated: IAddOnLibraryItem = { ...all[idx], ...patch, libraryId: all[idx].libraryId, createdAt: all[idx].createdAt, updatedAt: new Date().toISOString() };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  remove(libraryId: string): void {
    const all = this._library$.value.filter(i => i.libraryId !== libraryId);
    this.write(all);
  }

  /** Deletes the outgoing user's own per-email add-on library entirely,
   *  rather than just switching `storageKey` back to guest (which the
   *  `currentUser$` reaction in `refreshKey()` already does and would
   *  otherwise leave the per-email data orphaned in IDB). Runs on
   *  `loggedOut$`, which fires before `currentUser$` emits `null` — see
   *  quick-reply.service.ts's identical `wipeCurrentKey()` for why that
   *  ordering makes `this.storageKey` still correct here. */
  private wipeCurrentKey(): void {
    if (this.storageKey === GUEST_IDB_KEY) return;
    void this.store.delete(this.storageKey);
  }

  /** Swap the storage key when the signed-in user changes. Guest drafts
   *  migrate to the new key on first sign-in so unsigned work survives. */
  private refreshKey(): void {
    const email = this.auth.currentUser?.email;
    const newKey = email ? `${IDB_PREFIX}:${email}` : GUEST_IDB_KEY;
    if (newKey === this.storageKey) return;
    if (email && isPlatformBrowser(this.platformId)) {
      // Same priority order as quick-reply.service.ts: a pre-existing
      // per-email legacy bucket wins; migrateLegacyKey()'s synchronous cache
      // update (before its own first await) means the immediately-following
      // rekey() call already sees it and skips the guest fallback.
      void this.store.migrateLegacyKey(newKey, `${STORAGE_KEY_PREFIX}-${email}`);
      void this.store.rekey(GUEST_IDB_KEY, newKey);
    }
    this.storageKey = newKey;
    this._library$.next(this.read());
  }

  private read(): IAddOnLibraryItem[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    return this.store.read(this.storageKey) ?? [];
  }

  private write(items: IAddOnLibraryItem[]): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.write(this.storageKey, items);
    }
    this._library$.next(items);
  }
}
